import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Zap, BookOpen, Brain, Trophy, Users, Calendar, Sparkles, 
  ArrowRight, Check, Star, ChevronDown, MessageCircle,
  Target, Clock, Award, Video, Camera, TrendingUp,
  BarChart3, X, Flame, PlayCircle, ChevronRight
} from 'lucide-react';

const stats = [
  { value: '390+', label: 'NCERT CHAPTERS' },
  { value: '8', label: 'SUBJECTS COVERED' },
  { value: '7', label: 'CLASSES (6-12)' },
  { value: '10K+', label: 'QUIZ QUESTIONS' },
];

const features = [
  { 
    icon: Brain, 
    title: 'AI Chapter Essence', 
    desc: 'Instant summaries, key concepts & formulas for every chapter',
    color: 'violet'
  },
  { 
    icon: Sparkles, 
    title: 'Smart Quizzes', 
    desc: 'Dynamic questions that adapt to your learning - never repetitive!',
    color: 'amber'
  },
  { 
    icon: Users, 
    title: 'Group Study', 
    desc: 'Study with friends, compete in quiz battles & climb leaderboards',
    color: 'cyan'
  },
  { 
    icon: Calendar, 
    title: 'Exam Calendar', 
    desc: 'Track multiple exams with smart countdown & study recommendations',
    color: 'emerald'
  },
  { 
    icon: Camera, 
    title: 'Textbook OCR', 
    desc: 'Snap your textbook, get instant AI-powered study notes',
    color: 'pink'
  },
  { 
    icon: Video, 
    title: 'Video Explainers', 
    desc: 'AI-generated 3-min video lessons with voice narration',
    tag: 'Coming Soon',
    color: 'blue'
  },
];

const plans = [
  { 
    name: 'Basic', 
    monthlyPrice: 99, 
    yearlyPrice: 999,
    features: ['3 Subjects', 'Basic Quizzes', 'Parent Dashboard', 'Chapter Essence', 'Progress Tracking'],
    color: 'slate',
    popular: false
  },
  { 
    name: 'Pro', 
    monthlyPrice: 299, 
    yearlyPrice: 2999,
    features: ['All Subjects', 'Unlimited Quizzes', 'AI Video Explainers', 'Mock Exams', 'Group Study', 'Priority Support'],
    color: 'violet',
    popular: true
  },
  { 
    name: 'Family', 
    monthlyPrice: 499, 
    yearlyPrice: 4999,
    features: ['Everything in Pro', 'Up to 3 Children', 'Multi-Parent Access', 'Detailed Analytics', 'WhatsApp Alerts'],
    color: 'amber',
    popular: false
  },
];

const testimonials = [
  { name: 'Priya Sharma', role: 'Class 10 Student', text: "StudyClaw made exam prep actually fun! The AI quizzes helped me score 95% in Science.", avatar: '👧' },
  { name: 'Rajesh Mehta', role: 'Parent', text: "Finally I can track what my son is studying. The school sync feature is brilliant!", avatar: '👨' },
  { name: 'Mrs. Sunita Sharma', role: 'Teacher', text: "I update chapter progress and students get automatic revision nudges. Saves so much time!", avatar: '👩‍🏫' },
  { name: 'Arjun Reddy', role: 'Class 9 Student', text: "The group study competitions with my friends made studying so exciting!", avatar: '👦' },
];

const faqs = [
  { q: 'What curriculum does StudyClaw support?', a: 'StudyClaw covers the complete CBSE/NCERT curriculum for Classes 6-12, with 390+ chapters across Math, Science, Physics, Chemistry, Biology, English, Social Science, and Hindi.' },
  { q: 'How does the AI quiz work?', a: 'Our AI generates fresh, unique questions every time based on your chapter. It tracks what you\'ve answered before and ensures you always get new challenges!' },
  { q: 'Can parents track progress?', a: 'Absolutely! Parents can link their account to their child\'s, see real-time progress, exam countdowns, and even update school chapter progress.' },
  { q: 'Is there a free trial?', a: 'Yes! Every account gets a 14-day free trial of Pro features. No credit card required to start.' },
];

export default function LandingPage({ onGetStarted }) {
  const [openFaq, setOpenFaq] = useState(null);
  const [billingCycle, setBillingCycle] = useState('yearly');

  return (
    <div className="min-h-screen bg-slate-950 overflow-x-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-lg border-b border-slate-800/50">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-violet-500 flex items-center justify-center shadow-lg shadow-violet-500/20">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white tracking-tight">StudyClaw</span>
          </div>
          <button
            onClick={onGetStarted}
            className="bg-violet-600 hover:bg-violet-500 text-white px-5 py-2 rounded-full text-sm font-semibold transition-all shadow-lg shadow-violet-500/20"
            data-testid="header-get-started-btn"
          >
            Get Started
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-28 pb-16 px-4">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-96 h-96 bg-violet-600/20 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-cyan-600/10 rounded-full blur-[150px]" />
        </div>

        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight"
          >
            <span className="text-white">Your AI-Powered</span>
            <br />
            <span className="bg-gradient-to-r from-violet-400 via-purple-400 to-cyan-400 text-transparent bg-clip-text">
              Exam Superpower
            </span>
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-slate-400 text-base sm:text-lg max-w-2xl mx-auto mb-8"
          >
            Smart revision, instant quizzes, and progress tracking for CBSE Class 6–12 students. 
            Sync with school, study with friends, ace your exams!
          </motion.p>

          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            onClick={onGetStarted}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white px-8 py-4 rounded-2xl text-lg font-semibold transition-all shadow-xl shadow-violet-500/30 hover:shadow-violet-500/40 hover:scale-[1.02]"
            data-testid="get-started-btn"
          >
            <Zap className="w-5 h-5" />
            Start Learning Free
          </motion.button>

          <p className="text-slate-500 text-sm mt-4">14-day free trial • No credit card required</p>
        </div>

        {/* Stats Bar */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="max-w-3xl mx-auto mt-12"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((stat, i) => (
              <div key={i} className="bg-slate-900/50 border border-slate-800/50 rounded-2xl p-4 text-center backdrop-blur-sm">
                <div className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-violet-400 to-cyan-400 text-transparent bg-clip-text">
                  {stat.value}
                </div>
                <div className="text-xs text-slate-500 font-medium tracking-wider mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Problem/Solution Strip */}
      <section className="py-12 px-4">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
          {/* The Problem */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <X className="w-5 h-5 text-red-400" />
              <h3 className="text-lg font-bold text-white">The Problem</h3>
            </div>
            <ul className="space-y-3">
              {[
                'Students forget what they learned in school',
                'Parents can\'t track actual progress',
                'Boring revision leads to procrastination'
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-400 text-sm">
                  <X className="w-4 h-4 text-red-400/60 flex-shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* StudyClaw Solution */}
          <div className="bg-gradient-to-br from-violet-500/10 to-cyan-500/10 border border-violet-500/20 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Check className="w-5 h-5 text-emerald-400" />
              <h3 className="text-lg font-bold text-white">StudyClaw Solution</h3>
            </div>
            <ul className="space-y-3">
              {[
                'AI generates instant revision cards & quizzes',
                'Real-time progress visible to parents',
                'Gamified learning with XP, streaks & badges'
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-300 text-sm">
                  <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Everything you need to ace your exams</h2>
            <p className="text-slate-400">Powerful features designed for Indian students</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group bg-slate-900/50 border border-slate-800 hover:border-slate-700 rounded-2xl p-5 transition-all hover:bg-slate-900/80"
              >
                <div className={`w-11 h-11 rounded-xl bg-${feature.color}-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <feature.icon className={`w-5 h-5 text-${feature.color}-400`} />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-white">{feature.title}</h3>
                  {feature.tag && (
                    <span className="text-[10px] font-medium bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full">
                      {feature.tag}
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-400">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 px-4 bg-slate-900/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Simple, transparent pricing</h2>
            <p className="text-slate-400 mb-6">Start free, upgrade when you&apos;re ready</p>
            
            {/* Billing Toggle */}
            <div className="inline-flex items-center bg-slate-800/50 rounded-full p-1">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  billingCycle === 'monthly' 
                    ? 'bg-violet-600 text-white' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1 ${
                  billingCycle === 'yearly' 
                    ? 'bg-violet-600 text-white' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Yearly
                <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded-full">Save 20%</span>
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`relative rounded-2xl p-6 ${
                  plan.popular 
                    ? 'bg-gradient-to-b from-violet-600/20 to-slate-900/50 border-2 border-violet-500/50' 
                    : 'bg-slate-900/50 border border-slate-800'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-violet-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <h3 className="text-lg font-bold text-white mb-1">{plan.name}</h3>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-white">
                    ₹{billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice}
                  </span>
                  <span className="text-slate-400 text-sm">
                    /{billingCycle === 'monthly' ? 'month' : 'year'}
                  </span>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-slate-300">
                      <Check className="w-4 h-4 text-violet-400" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={onGetStarted}
                  className={`w-full py-3 rounded-xl font-semibold transition-all ${
                    plan.popular
                      ? 'bg-violet-600 hover:bg-violet-500 text-white shadow-lg shadow-violet-500/20'
                      : 'bg-slate-800 hover:bg-slate-700 text-white'
                  }`}
                >
                  Start Free Trial
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Loved by students & parents</h2>
            <p className="text-slate-400">Join thousands of families across India</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-xl">
                    {t.avatar}
                  </div>
                  <div>
                    <div className="font-semibold text-white text-sm">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.role}</div>
                  </div>
                </div>
                <p className="text-slate-400 text-sm">&ldquo;{t.text}&rdquo;</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-slate-900/30">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Frequently asked questions</h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div 
                key={i}
                className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full px-5 py-4 flex items-center justify-between text-left"
                >
                  <span className="font-medium text-white text-sm">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-sm text-slate-400">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Ready to transform your exam prep?
          </h2>
          <p className="text-slate-400 mb-8">
            Join thousands of students who&apos;ve improved their grades with StudyClaw
          </p>
          <button
            onClick={onGetStarted}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white px-8 py-4 rounded-2xl text-lg font-semibold transition-all shadow-xl shadow-violet-500/30"
            data-testid="final-cta-btn"
          >
            <Zap className="w-5 h-5" />
            Start Learning Free
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-800">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-white">StudyClaw</span>
            <span className="text-slate-500 text-sm">v4.2</span>
          </div>
          <p className="text-slate-500 text-sm">
            © 2026 StudyClaw. AI Exam Coach for CBSE.
          </p>
        </div>
      </footer>
    </div>
  );
}
